import React, { useState } from "react";
import { Building2, Users, FileText, ExternalLink, ChevronRight } from "lucide-react";
import { PageHero, PageBody, FONT } from "./_shared";
import { useBul } from "./BulLayout";

export default function Committees() {
  const data = useBul();
  const list = data.committees || [];
  const [activeId, setActiveId] = useState(list[0]?.id || null);

  const activeIdx = Math.max(0, list.findIndex((c) => c.id === activeId));
  const active = list[activeIdx] || null;

  return (
    <div data-testid="bul-committees">
      <PageHero eyebrow="Governance" title="Committees & Cells" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white p-16 border border-[#D9D2C1] text-[#726C61]">
            Committee details will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-6">
            <aside
              className="bg-white border border-[#D9D2C1] md:sticky md:top-24 self-start"
              data-testid="bul-committees-list"
            >
              <div className="px-5 py-4 border-b border-[#D9D2C1]">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold">Browse</div>
                <div className="mt-1 text-lg text-[#0F0F10]" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>
                  All Committees ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((c) => {
                  const isActive = c.id === active?.id;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(c.id)}
                        data-testid={`bul-committee-tab-${c.id}`}
                        className={`w-full text-left px-5 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive ? "bg-[#F8F5EE] border-[#FDBB30]" : "border-transparent hover:bg-[#F8F5EE]"
                        }`}
                      >
                        <span className="w-8 h-8 bg-[#0F0F10] text-[#FDBB30] flex items-center justify-center shrink-0">
                          <Building2 size={14} strokeWidth={1.6} />
                        </span>
                        <span className={`flex-1 text-sm leading-snug ${isActive ? "text-[#0F0F10] font-semibold" : "text-[#2A2A2B]"}`}>
                          {c.name}
                        </span>
                        <ChevronRight size={14} strokeWidth={1.6} className={isActive ? "text-[#FDBB30]" : "text-[#D9D2C1]"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {active && (
              <article
                key={active.id}
                data-testid={`bul-committee-${active.id}`}
                className="bg-white border border-[#D9D2C1]"
              >
                <div className="bg-[#0F0F10] p-6 text-white flex items-center gap-4 border-b-4 border-[#FDBB30]">
                  <div className="w-11 h-11 border-2 border-[#FDBB30] flex items-center justify-center text-[#FDBB30]">
                    <Building2 size={18} strokeWidth={1.6} />
                  </div>
                  <h2 className="text-2xl md:text-3xl leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>{active.name}</h2>
                </div>
                <div className="p-7">
                  {active.objective_html && (
                    <div className="mb-6">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-2">Objective</div>
                      <div className="prose prose-sm max-w-none text-[#2A2A2B] leading-[1.9]" dangerouslySetInnerHTML={{ __html: active.objective_html }} />
                    </div>
                  )}
                  {active.has_functions && active.functions_html && (
                    <div className="mb-6">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-2">Functions</div>
                      <div className="prose prose-sm max-w-none text-[#2A2A2B] leading-[1.9]" dangerouslySetInnerHTML={{ __html: active.functions_html }} />
                    </div>
                  )}
                  {(active.members || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-3 inline-flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.6} /> Members ({active.members.length})
                      </div>
                      <ul className="divide-y divide-[#D9D2C1] border border-[#D9D2C1]">
                        {active.members.map((m, idx) => (
                          <li key={idx} className="px-4 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <span className="font-medium text-[#0F0F10]">{m.name}</span>
                            {m.department && <span className="text-[#726C61] text-xs">{m.department}</span>}
                            {m.designation && (
                              <span className="sm:ml-auto text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-[#0F0F10] text-white font-semibold">
                                {m.designation}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.has_pdf && active.pdf_url && (
                    <a
                      href={active.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] px-4 py-2.5"
                    >
                      <FileText size={14} strokeWidth={2} /> Open attached PDF <ExternalLink size={11} strokeWidth={2} />
                    </a>
                  )}
                  {active.remarks && (
                    <div className="mt-5 text-xs text-[#726C61] italic">{active.remarks}</div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
